/**
 * PHARMAKON — formatting helpers
 *
 * Ported from the design's lib/format.js + config/locale.config.js
 * (formatters are mixed across the two in the prototype; consolidated here).
 *
 * Locale: Algerian French. Currency: Dinar Algérien (DA).
 */
import { LOCALE_LABELS } from "../config/locale.config.js";

// ── Role / status labels ────────────────────────────────────────────────────

export const formatRoleLabel = (role) => LOCALE_LABELS?.[role] || role;
export const formatStatusLabel = (status) => LOCALE_LABELS?.[status] || status;

// ── Time helpers ────────────────────────────────────────────────────────────

// Milliseconds per day — daysUntil arithmetic (kept here, config-layer style:
// no magic numbers inline).
const MS_PER_DAY = 86_400_000;

/**
 * Days from today until `dateStr`. Returns null when input is missing.
 *
 * R3-H8 — the design's frozen demo clock (`today = new Date("2026-05-26")`)
 * shipped to production and mislabelled every lot's expiry badge (an expired
 * lot showed "SAIN", drifting further daily). The clock is now the REAL
 * `new Date()`; `now` is injectable (tests / any future demo mode) and
 * defaults to the wall clock.
 */
export const daysUntil = (dateStr, now = new Date()) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / MS_PER_DAY);
};

/** Per-lot status used by stock pages to colour rows. Same injectable clock. */
export const lotStatus = (lot, now = new Date()) => {
  if (!lot) return "unknown";
  const days = daysUntil(lot.date_peremption, now);
  if (days < 0) return "expired";
  if (lot.quantite_disponible <= 0) return "empty";
  if (days <= 30) return "expiring-soon";
  return "healthy";
};

/** Money column for cashier role — design hides the value. */
export const maskMoneyForRole = (value, role) => {
  if (role === "CAISSIER") return null;
  return value;
};

// Re-export the locale-config formatters so callers only need to import
// from one place.
export {
  fmtMoney,
  fmtInt,
  fmtPct,
  fmtDate,
  fmtDateLong,
  fmtTime,
  fmtRelTime,
} from "../config/locale.config.js";
